/**
 * THE CABINET'S SUPPORT PAGE.  Route `/support`.
 *
 * Operations: `getPlatformContacts`, `getCurrentTerms`, `getAuthorSession` (from the shell).
 * NOT called here: `acceptTerms` — acceptance belongs to the interstitial, and a help page is not
 * where somebody agrees to a contract. Also NOT called here: `getPaymentsView`, which would be a
 * money read on a help page, 403-gated, for one number.
 *
 * WHAT THE PAGE IS. LOCKED D37 (`00-scope-v1.md`) and erratum G28 (:463) between them make this
 * a page of link-outs plus one contact block: the guides live on moonx.app and the cabinet hosts
 * only authenticated surfaces, and tickets and chat are deferred with e-mail as the v1 answer. So
 * the whole page holds exactly two pieces of data, and everything else is copy.
 *
 * LOADING IS PARTIAL BY DESIGN. The five cards and the three answers are static and paint on the
 * first frame. Only the terms card's live half and the contact block resolve, independently, and
 * neither can blank the other. A 403 on the contacts read leaves the guides and the terms alone;
 * a 503 on the terms read leaves the addresses alone.
 *
 * THE ONE SENTENCE THAT MUST NOT BE GOT WRONG. When re-acceptance is owed, this page says what is
 * blocked AND what is not: sending an article for review is paused; earnings and payouts are not.
 * It is said of the submission documents (the author agreement, the editorial standards) - "with-
 * holding money already earned because somebody has not clicked a checkbox is a serious act"
 * (`04-schema.md`); only design 25's payout documents and Payment details can hold one.
 */
import { Alert, Anchor, Button, Group, Stack, Text } from '@mantine/core';
import { Trans, useTranslation } from '../../i18n';
import { isRetryable, isUnauthenticated } from './api/errors';
import { IS_FIXTURE_TRANSPORT } from './api/transport';
import { ContactBlock } from './components/ContactBlock';
import { GuideCards } from './components/GuideCards';
import { QuickAnswers } from './components/QuickAnswers';
import { FailureBlock } from './components/StateBlocks';
import { useCabinetSession } from './hooks/use-cabinet-session';
import { useCurrentTerms, usePlatformContacts } from './hooks/use-support-data';
import { CABINET_ROUTES, useNavigate } from './navigation';
import classes from './support.module.css';

export function SupportPage() {
  const { t } = useTranslation('support');
  const navigate = useNavigate();
  const session = useCabinetSession();
  const contacts = usePlatformContacts();
  const terms = useCurrentTerms();

  /**
   * Re-acceptance is owed when the session says so. `termsAcceptanceRequired` is optional on
   * `AuthorSession`, so `=== true` and not a truthiness test — an absent field is not a false one,
   * and the fallback below is what covers it.
   *
   * The terms read is a second, independent witness of the same fact and is used only when the
   * session did not answer. It is deliberately NOT used to contradict a session that said no.
   */
  const sessionSaysOwed = session.data?.termsAcceptanceRequired === true;
  const sessionIsSilent =
    session.status !== 'ready' || session.data?.termsAcceptanceRequired === undefined;
  const termsSayOwed = terms.status === 'ready' && terms.data !== null && !terms.data.accepted;
  const reacceptanceOwed = sessionSaysOwed || (sessionIsSilent && termsSayOwed);

  const sessionExpired =
    (session.status === 'failed' && session.error !== null && isUnauthenticated(session.error)) ||
    (terms.status === 'failed' && terms.error !== null && isUnauthenticated(terms.error));

  const goToTerms = () => {
    navigate(CABINET_ROUTES.terms);
  };
  const goToSignIn = () => {
    navigate(CABINET_ROUTES.signIn);
  };

  // ★ Drawn as the prototype's Support view (the owner's live look at real cabinets, 2026-10-01 /
  // design 15): the title at its size, the guides and the answers as cards under small capitals,
  // and the contact block as a calm card. Nothing it reads or says changed but the plain words.
  return (
    <div className={classes.page}>
      <Stack gap="xl">
        <header>
          <h1 className={classes.pageTitle}>{t('page.pageTitle')}</h1>
          <p className={classes.lede}>{t('page.pageIntro')}</p>
        </header>

        {IS_FIXTURE_TRANSPORT ? (
          // A standing, deliberately ugly marker: nothing on screen came from the platform. It
          // disappears in a build with the one live flag set, which is the same decision that
          // turns the real fetch on (api/transport.ts, `IS_FIXTURE_TRANSPORT`).
          <p className={classes.fixtureNotice}>
            Fixture data. This build does not call MoonX — the e-mail addresses and terms below are
            stand-ins until VITE_AUTHORS_API_LIVE is set (features/support/api/transport.ts).
          </p>
        ) : null}

        {sessionExpired ? (
          <FailureBlock
            title={t('page.sessionExpiredTitle')}
            body={t('page.sessionExpiredBody')}
            onRetry={goToSignIn}
            retryLabel={t('page.signInAgain')}
          />
        ) : null}

        {reacceptanceOwed && !sessionExpired ? (
          <Alert role="alert" variant="light" color="moon" title={t('page.termsBlocksHeading')}>
            <Stack gap="sm" align="flex-start">
              <Text size="sm">{t('page.termsBlocksBody')}</Text>
              <Button size="xs" radius="xl" variant="filled" onClick={goToTerms}>
                {t('page.termsReviewAndAccept')}
              </Button>
            </Stack>
          </Alert>
        ) : null}

        <section aria-labelledby="support-guides-heading">
          <Group justify="space-between" align="center" mb={12}>
            <h2 id="support-guides-heading" className={classes.eyebrow}>
              {t('page.guidesHeading')}
            </h2>
            {/* One retry for the one live thing on the card list, rather than five identical
                buttons. Only offered when retrying could plausibly help. */}
            {terms.status === 'failed' && terms.error !== null && isRetryable(terms.error) ? (
              <Button
                size="compact-xs"
                variant="default"
                onClick={terms.reload}
                loading={terms.reloading}
              >
                {t('page.termsRetry')}
              </Button>
            ) : null}
          </Group>
          <GuideCards terms={terms} onReviewAndAccept={goToTerms} />
        </section>

        <section aria-labelledby="support-answers-heading">
          <h2 id="support-answers-heading" className={classes.eyebrow} style={{ marginBottom: 12 }}>
            {t('page.quickAnswersHeading')}
          </h2>
          <QuickAnswers />
          <Text size="xs" c="dimmed" mt="sm">
            <Trans
              t={t}
              i18nKey="page.figuresOnPayments"
              components={{
                payments: (
                  <Anchor
                    size="xs"
                    component="button"
                    type="button"
                    onClick={() => {
                      navigate(CABINET_ROUTES.payments);
                    }}
                  />
                ),
              }}
            />
          </Text>
        </section>

        <ContactBlock contacts={contacts} onSessionExpired={goToSignIn} />
      </Stack>
    </div>
  );
}
